<script setup lang="ts">
import { computed, ref } from "vue";
import { Plus } from "@lucide/vue";
import { LocalModelType } from "@/features/BuildingProject";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import SubCardHeading from "../../SubCardHeading.vue";

const props = withDefaults(
  defineProps<{
    /** Every model name on the list already, active or not, for the duplicate check. */
    existingModelNames: Array<string>;
    /** Just the add row, under a list that already has its own label and caption (the start dialog). */
    compact?: boolean;
  }>(),
  { compact: false }
);

const emit = defineEmits<{
  addNewModel: [model: LocalModelType];
}>();

const currentModelName = ref<string>("");

const trimmedName = computed(() => currentModelName.value.trim());
const isDuplicate = computed(() =>
  props.existingModelNames.some((existingName) => existingName.toLowerCase() === trimmedName.value.toLowerCase())
);
const errorMessage = computed(() => (isDuplicate.value ? `A model named "${trimmedName.value}" already exists.` : ""));
const canAdd = computed(() => trimmedName.value.length > 0 && !isDuplicate.value);

const handleAddCustomModel = () => {
  if (!canAdd.value) return;
  emit("addNewModel", { name: trimmedName.value, isActive: true });
  currentModelName.value = "";
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <template v-if="!props.compact">
      <SubCardHeading class="t-small-bold text-type-default">Create a model to publish into</SubCardHeading>
      <p class="t-caption text-type-muted max-w-prose">
        This creates an empty model with the connection. Geometry arrives when you publish to it from the Speckle connector. A
        model is usually one trade or discipline, for example Architecture, Structure or MEP.
      </p>
    </template>
    <!-- items-end, not items-start + a guessed offset: aligns the button to the input's own box
         regardless of what sits above it (a label here, a label plus caption in the non-compact card). -->
    <div class="flex items-end gap-2">
      <div :class="[compact ? '' : 'max-w-xs', 'flex-1']">
        <TextInputNew
          v-model="currentModelName"
          label="Model name"
          placeholder="e.g. Architecture"
          :error-msg="errorMessage"
          :remove-leading-whitespace="true"
          @keydown.enter.prevent="handleAddCustomModel" />
      </div>
      <button
        type="button"
        aria-label="Create model"
        class="focus-ring interactive-active duration-fast ease-standard rounded-8 t-small-bold text-type-default flex h-9 shrink-0 items-center gap-1 bg-slate-200 px-4 whitespace-nowrap transition-colors hover:bg-slate-300"
        :class="{ 'interactive-disabled': !canAdd }"
        :disabled="!canAdd"
        @click="handleAddCustomModel">
        <Plus
          class="icon-sm"
          :stroke-width="1.5"
          aria-hidden="true" />
        <span>Create model</span>
      </button>
    </div>
  </div>
</template>
